/**
 * Talking to the companion, for as long as the player wants.
 *
 * What was here before was a single shot: tap the mic, say one thing, the
 * recognizer ends and nothing ever starts it again. Every reply was the end of
 * the conversation, which is why she seemed to stop answering after the first
 * or second message. She was answering fine. Nobody was listening for what came
 * next.
 *
 * This is a loop instead. Listen, hear a turn, let her answer, wait until she
 * has actually finished making sound, listen again. It runs until the player
 * ends it, and the only thing that stops it on its own is a long silence.
 *
 * Three ways to hear, the first that works:
 *   stream   the mic streamed to Deepgram Flux on Workers AI (/api/voice/ears),
 *            which transcribes as they talk and ends the turn itself, about
 *            half a second after the last word. No silence timer, no upload.
 *   web      the browser's own recognizer. Free, instant, no round trip.
 *   recorder MediaRecorder plus /api/listen (Whisper). Slower, works anywhere.
 *
 * The one rule underneath all of it: NEVER listen while she is speaking. A
 * phone speaker is inches from its microphone, so an open mic hears her voice,
 * transcribes it, and sends it back as if the player had said it. That is how
 * a voice loop ends up talking to itself.
 */

const MAX_TURN_MS = 15000;        // longest single thing anyone says in one breath
const HUSH_MS = 1200;             // trailing quiet that means they have finished (the fallback when Smart Turn has no answer)
// Smart Turn: at a short pause the last few seconds of speech go to
// @cf/pipecat-ai/smart-turn-v2 on Workers AI, which says whether the thought
// ended. A yes ends the turn right there, most of a second before HUSH_MS
// would have. A no, a slow answer or no answer leaves the timer in charge.
const PROBE_AFTER_MS = 320;       // quiet before the first probe
const PROBE_EVERY_MS = 650;       // and between probes while the pause goes on
const PROBE_WINDOW_S = 6;         // seconds of audio sent, from a ring buffer
const PROBE_CONFIDENT = 0.6;      // is_complete with at least this probability
const RING_RATE = 16000;          // the model wants 16 kHz mono 16 bit
const WAIT_FOR_VOICE_MS = 9000;   // opened the mic and heard nothing at all
const QUIET_TURNS_TO_STOP = 3;    // three of those in a row and we stop listening
const TAIL_MS = 260;              // let the last of her voice die before reopening
const SPEECH_RMS = 0.022;         // above this counts as someone talking
const MIN_VOICED_MS = 260;        // shorter than this was a noise, not a word
// Where a conversation runs is the Worker's call: POST /api/voice/session says
// cf (the voice agent inside Cloudflare, voicecf.js), webrtc (a Hooper Buddy
// voice node, voicertc.js), browser (this file, as always), or 402 (talk time
// used up). No clear answer means the browser.
const SESSION_URL = '/api/voice/session';
const SESSION_WAIT_MS = 2500;     // a tap nobody prefetched for waits this long before the browser lane (was 1200)
const WEBRTC_CONNECT_MS = 4000;   // slower than this and the browser lane takes over
const CF_CONNECT_MS = 10000;      // the voice agent may start a container for this call (5.5 to 7.5 s cold); slower and the browser lane takes over

const isIOS = () => /iP(hone|ad|od)/.test(navigator.userAgent)
  || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

const SR = () => window.SpeechRecognition || window.webkitSpeechRecognition;

/**
 * iOS ships the Web Speech API but it is not dependable: it stops mid turn, it
 * re-asks for permission, and it is absent altogether from iOS Chrome and from
 * a page saved to the home screen. Recording and sending the audio is slower by
 * about a second and it works every time, so that is what a phone gets.
 */
const preferRecorder = () => isIOS() || !SR();

/* ----------------------------------------------------------- the ear: stream */

const EARS_URL = '/api/voice/ears';
const EARS_OPEN_MS = 2500;        // slower than this to open and the next ear takes over
const EARS_FRAME = 1280;          // 80 ms of 16 kHz audio a message
const streamable = () => {
  const AC = window.AudioContext || window.webkitAudioContext;
  return typeof WebSocket !== 'undefined' && !!AC && 'audioWorklet' in AC.prototype;
};

/**
 * The mic, streamed to Flux through the Worker for as long as the conversation
 * lasts. Between turns, while she talks, it sends silence instead of the mic:
 * the socket and Flux's clock stay alive, and her voice coming out of the
 * speaker is never transcribed as the player. A turn is the next EndOfTurn
 * that has words in it.
 */
function createStreamEar() {
  let stream = null, ctx = null, ws = null, dead = false, listening = false, waiter = null;
  const frame = new Int16Array(EARS_FRAME); let fill = 0, step = 3, acc = 0, n = 0, pos = 0;

  const send = () => { if (ws?.readyState === 1) ws.send(frame.slice(0, fill).buffer); fill = 0; };
  // Down to 16 kHz by averaging each output sample's share of the input. Picking
  // every third sample instead (fine for Smart Turn's yes or no) folded the
  // high end back into the speech band, and Flux ended turns early on it.
  function push(f32) {
    for (let k = 0; k < f32.length; k++) {
      acc += f32[k]; n++; pos += 1;
      if (pos < step) continue;
      pos -= step;
      const v = listening ? Math.max(-1, Math.min(1, acc / n)) : 0;
      acc = 0; n = 0;
      frame[fill++] = v < 0 ? v * 0x8000 : v * 0x7fff;
      if (fill === EARS_FRAME) send();
    }
  }

  function onMessage(ev) {
    let j; try { j = JSON.parse(typeof ev.data === 'string' ? ev.data : new TextDecoder().decode(ev.data)); } catch { return; }
    if (!waiter || j.type !== 'TurnInfo') return;
    const text = String(j.transcript || '').trim();
    if (j.event === 'StartOfTurn' || (j.event === 'Update' && text)) { waiter.heard = true; if (text) { waiter.latest = text; waiter.onPartial?.(text); } }
    // Flux's early guess that the turn is over (eager_eot_threshold in
    // worker/ears.js), a few hundred ms before EndOfTurn, and its retraction.
    if (j.event === 'EagerEndOfTurn' && text) waiter.onEager?.(text);
    if (j.event === 'TurnResumed') waiter.onResumed?.();
    if (j.event === 'EndOfTurn' && text) waiter.resolve(text);
  }

  async function open() {
    stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
    const AC = window.AudioContext || window.webkitAudioContext;
    ctx = new AC();
    if (ctx.state === 'suspended') { try { await ctx.resume(); } catch {} }
    step = ctx.sampleRate / RING_RATE;
    await ctx.audioWorklet.addModule(TAP_WORKLET);
    const tap = new AudioWorkletNode(ctx, 'hb-tap', { numberOfOutputs: 0 });
    ctx.createMediaStreamSource(stream).connect(tap);
    ws = new WebSocket(`${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}${EARS_URL}`);
    ws.binaryType = 'arraybuffer';
    await new Promise((resolve, reject) => {
      const t = setTimeout(() => reject(new Error('The streaming ears did not open.')), EARS_OPEN_MS);
      ws.onopen = () => { clearTimeout(t); resolve(); };
      ws.onerror = () => { clearTimeout(t); reject(new Error('The streaming ears are not available.')); };
    });
    ws.onmessage = onMessage;
    ws.onclose = () => { dead = true; waiter?.fail(Object.assign(new Error('The streaming ears closed.'), { code: 'ears' })); };
    tap.port.onmessage = (e) => push(e.data);
  }

  /** Resolves with what they said, or '' after a silence, a cap or a cancel. */
  async function turn(onPartial, cancelled, { onEager, onResumed } = {}) {
    if (dead) throw Object.assign(new Error('The streaming ears closed.'), { code: 'ears' });
    listening = true;
    try {
      return await new Promise((resolve, reject) => {
        const began = performance.now();
        const w = { heard: false, latest: '', onPartial, onEager, onResumed };
        const tick = setInterval(() => {
          const age = performance.now() - began;
          if (cancelled()) w.resolve('');
          else if (!w.heard && age > WAIT_FOR_VOICE_MS) w.resolve('');
          else if (age > MAX_TURN_MS) w.resolve(w.latest);
        }, 200);
        w.resolve = (t) => { clearInterval(tick); waiter = null; resolve(t); };
        w.fail = (e) => { clearInterval(tick); waiter = null; reject(e); };
        waiter = w;
      });
    } finally { listening = false; }
  }

  function close() {
    listening = false; waiter = null; dead = true;
    try { ws?.close(); } catch {}
    try { stream?.getTracks().forEach((t) => t.stop()); } catch {}
    try { ctx?.close(); } catch {}
    ws = null; stream = null; ctx = null;
  }

  return { open, turn, close };
}

/* -------------------------------------------------------------- the ear: web */

/**
 * One turn from the browser's own recognizer. Resolves with what was said, or
 * '' when the turn passed without words. Rejects only on something fatal, which
 * means the caller should stop trying this engine.
 */
function webTurn(lang, onPartial) {
  return new Promise((resolve, reject) => {
    const rec = new (SR())();
    rec.lang = lang;
    rec.interimResults = true;
    rec.continuous = false;
    let said = '', settled = false;
    const done = (v) => { if (settled) return; settled = true; try { rec.stop(); } catch {} resolve(v); };

    rec.onresult = (e) => {
      let interim = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i];
        if (r.isFinal) said += r[0].transcript;
        else interim += r[0].transcript;
      }
      onPartial?.((said + interim).trim());
      if (said.trim()) done(said.trim());
    };
    rec.onerror = (e) => {
      // Nothing said and the player walking away are both ordinary.
      if (e.error === 'no-speech' || e.error === 'aborted') return done('');
      if (settled) return;
      settled = true;
      const fatal = new Error(
        e.error === 'not-allowed' || e.error === 'service-not-allowed'
          ? 'Microphone access is off for this site. Turn it on in your browser settings.'
          : 'The browser could not listen.');
      fatal.code = e.error;
      reject(fatal);
    };
    rec.onend = () => done(said.trim());

    try { rec.start(); } catch (e) { if (!settled) { settled = true; reject(e); } }
    // The recognizer is supposed to end itself on silence. When it does not,
    // this is what keeps a turn from hanging forever.
    setTimeout(() => { try { rec.stop(); } catch {} }, MAX_TURN_MS + 2000);
  });
}

/* --------------------------------------------------------- the ear: recorder */

const MIMES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/aac', ''];
const pickMime = () => MIMES.find((m) => !m || (window.MediaRecorder?.isTypeSupported?.(m))) ?? '';

/**
 * Holds the microphone open across turns. Asking for it once means one
 * permission prompt for the whole conversation and no gap between turns while
 * the browser wakes the mic up again.
 */
/* A tiny AudioWorklet that hands the main thread raw samples. The recorder
 * itself only keeps compressed chunks (webm on Chrome, mp4 on iOS) that cannot
 * be read back mid turn, so this is the one place the page sees PCM. It is a
 * same-origin file: see public/worklets/hb-tap.js for why not a blob. */
const TAP_WORKLET = '/worklets/hb-tap.js';

/** The last PROBE_WINDOW_S seconds of the mic, resampled to 16 kHz Int16, as a WAV. */
function createRing(sampleRate) {
  const size = RING_RATE * PROBE_WINDOW_S;
  const buf = new Int16Array(size); let head = 0, filled = 0;
  const step = sampleRate / RING_RATE; let carry = 0;
  return {
    push(f32) {
      // Cheap decimation: every step-th sample. The gate and the model both
      // care about speech shape, not hi-fi, and this costs nothing per frame.
      for (let i = carry; i < f32.length; i += step) {
        const v = Math.max(-1, Math.min(1, f32[Math.floor(i)]));
        buf[head] = v < 0 ? v * 32768 : v * 32767; head = (head + 1) % size; if (filled < size) filled++;
      }
      carry = (carry + f32.length) % step; if (carry >= 1) carry -= Math.floor(carry);
    },
    wavBase64(seconds = PROBE_WINDOW_S) {
      const n = Math.min(filled, Math.round(seconds * RING_RATE));
      const out = new Int16Array(n); let j = (head - n + size) % size;
      for (let i = 0; i < n; i++) { out[i] = buf[j]; j = (j + 1) % size; }
      const bytes = new Uint8Array(44 + n * 2); const dv = new DataView(bytes.buffer);
      const w = (o, t) => { for (let i = 0; i < t.length; i++) bytes[o + i] = t.charCodeAt(i); };
      w(0, 'RIFF'); dv.setUint32(4, 36 + n * 2, true); w(8, 'WAVE'); w(12, 'fmt '); dv.setUint32(16, 16, true); dv.setUint16(20, 1, true); dv.setUint16(22, 1, true);
      dv.setUint32(24, RING_RATE, true); dv.setUint32(28, RING_RATE * 2, true); dv.setUint16(32, 2, true); dv.setUint16(34, 16, true); w(36, 'data'); dv.setUint32(40, n * 2, true);
      bytes.set(new Uint8Array(out.buffer), 44);
      let bin = ''; for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
      return btoa(bin);
    },
    seconds: () => filled / RING_RATE,
  };
}

/** Ask the server whether the pause is the end of the thought. Null means no opinion. */
async function probeTurn(ring) {
  if (!ring || ring.seconds() < 0.8) return null;
  const ac = new AbortController(); const t = setTimeout(() => ac.abort(), 1200);
  try {
    const r = await fetch('/api/turn', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ audio: ring.wavBase64() }), signal: ac.signal });
    if (!r.ok) return null;
    const j = await r.json();
    return { complete: j.complete === true && j.probability >= PROBE_CONFIDENT, probability: j.probability, ms: j.ms };
  } catch { return null; }
  finally { clearTimeout(t); }
}

function createRecorderEar() {
  let stream = null, ctx = null, analyser = null, data = null, ring = null;

  async function open() {
    if (stream) return;
    stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    });
    const AC = window.AudioContext || window.webkitAudioContext;
    ctx = new AC();
    // Safari starts the context suspended until a gesture has touched it.
    if (ctx.state === 'suspended') { try { await ctx.resume(); } catch {} }
    analyser = ctx.createAnalyser();
    analyser.fftSize = 1024;
    data = new Uint8Array(analyser.fftSize);
    const source = ctx.createMediaStreamSource(stream);
    source.connect(analyser);
    // The PCM tap for Smart Turn. Optional: a browser without worklets keeps
    // the timer and loses nothing it had before.
    try {
      if (ctx.audioWorklet) {
        await ctx.audioWorklet.addModule(TAP_WORKLET);
        const tap = new AudioWorkletNode(ctx, 'hb-tap', { numberOfOutputs: 0 });
        ring = createRing(ctx.sampleRate);
        tap.port.onmessage = (e) => ring.push(e.data);
        source.connect(tap);
      }
    } catch (e) { console.warn('turn tap unavailable', e?.message); ring = null; }
  }

  const level = () => {
    if (!analyser) return 0;
    analyser.getByteTimeDomainData(data);
    let sum = 0;
    for (let i = 0; i < data.length; i++) { const v = (data[i] - 128) / 128; sum += v * v; }
    return Math.sqrt(sum / data.length);
  };

  /** Record until they stop talking. Resolves with a Blob, or null if silent. */
  async function turn(onLevel, cancelled) {
    await open();
    const type = pickMime();
    const rec = new MediaRecorder(stream, type ? { mimeType: type } : undefined);
    const chunks = [];
    rec.ondataavailable = (e) => { if (e.data?.size) chunks.push(e.data); };
    const stopped = new Promise((res) => { rec.onstop = res; });
    rec.start(120);

    const began = performance.now();
    let heard = false, lastLoud = began, voiced = 0, prev = began;
    let probing = false, lastProbe = 0, decided = false;
    await new Promise((res) => {
      const done = () => { if (!decided) { decided = true; res(); } };
      const tick = () => {
        if (decided) return;
        if (cancelled()) return done();
        const now = performance.now(), l = level();
        onLevel?.(l);
        if (l > SPEECH_RMS) { heard = true; voiced += now - prev; lastLoud = now; }
        prev = now;
        if (!heard && now - began > WAIT_FOR_VOICE_MS) return done();
        if (heard && now - lastLoud > HUSH_MS) return done();
        if (now - began > MAX_TURN_MS) return done();
        // A pause worth asking about: Smart Turn answers in a few hundred ms,
        // well inside the HUSH_MS the timer would still make them wait.
        const quiet = now - lastLoud;
        if (ring && heard && voiced >= MIN_VOICED_MS && quiet >= PROBE_AFTER_MS && !probing && now - lastProbe >= PROBE_EVERY_MS) {
          probing = true; lastProbe = now; const askedAt = lastLoud;
          probeTurn(ring).then((v) => {
            probing = false;
            // Only if they have stayed quiet since the audio we sent.
            if (v?.complete && !decided && lastLoud === askedAt) { turn.lastProbe = v; done(); }
          });
        }
        requestAnimationFrame(tick);
      };
      tick();
    });

    try { rec.stop(); } catch {}
    await stopped;
    if (!heard || cancelled()) return null;
    // A blip too short to be a word is a door, a cough or a bounce.
    if (voiced < MIN_VOICED_MS) return null;
    return { blob: new Blob(chunks, { type: type || 'audio/webm' }), voiced: Math.round(voiced) };
  }

  function close() {
    try { stream?.getTracks().forEach((t) => t.stop()); } catch {}
    try { ctx?.close(); } catch {}
    stream = null; ctx = null; analyser = null; ring = null;
  }

  return { turn, close, open };
}

/**
 * getUserMedia's own messages are things like "Permission denied", which tells
 * a player nothing about what to do. Say what happened and what fixes it.
 */
function micError(e) {
  const name = e?.name || '';
  const msg = name === 'NotAllowedError' || name === 'SecurityError'
    ? 'Microphone access is off for this site. Turn it on in your browser settings, then tap the mic again.'
    : name === 'NotFoundError' || name === 'OverconstrainedError'
      ? 'No microphone found on this device. You can still type to your companion.'
      : name === 'NotReadableError'
        ? 'Something else is using the microphone. Close it and tap the mic again.'
        : 'The microphone would not open. You can still type to your companion.';
  const out = new Error(msg);
  out.code = 'mic';
  return out;
}

const toDataUrl = (blob) => new Promise((res, rej) => {
  const fr = new FileReader();
  fr.onload = () => res(String(fr.result));
  fr.onerror = () => rej(new Error('That recording could not be read.'));
  fr.readAsDataURL(blob);
});

/* ----------------------------------------------------------- the conversation */

/**
 * The session is asked for ahead of the tap: a first visit's answer took 2.3 s
 * on production (2026-09-30), which the tap cannot wait for, so opening her room
 * or Train asks early, without a deadline anyone feels. A token lives 120 s
 * (worker/voicepool.js); one under 90 s old is used by the next start.
 */
const EARLY_WAIT_MS = 5000, EARLY_FRESH_MS = 90 * 1000;
let early = null;
export function prefetchSession() {
  if (early && Date.now() - early.at < EARLY_FRESH_MS / 2) return;
  early = { at: Date.now(), p: voiceSession(EARLY_WAIT_MS) };
}
function takeEarly() {
  const e = early; early = null;
  return e && Date.now() - e.at < EARLY_FRESH_MS ? e.p : null;
}

/** Ask the Worker where this conversation runs. Anything but a clear answer is the browser, as today. */
async function voiceSession(waitMs = SESSION_WAIT_MS) {
  const ac = new AbortController(); const t = setTimeout(() => ac.abort(), waitMs);
  try {
    const r = await fetch(SESSION_URL, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ lanes: ['cf', 'webrtc'] }), signal: ac.signal });
    const j = await r.json().catch(() => ({}));
    if (r.status === 402) return { mode: 'exhausted', message: j.error || 'You have used all your talk time for this month.', packs: j.packs };
    if (r.ok && (j.mode === 'webrtc' || j.mode === 'cf') && j.url && j.token) return j;
  } catch {}
  finally { clearTimeout(t); }
  return { mode: 'browser' };
}

/**
 * @param ask        (text) => Promise, resolves when the companion has finished her reply
 * @param isSpeaking () => boolean, true while any of her audio is still playing
 * @param onState    (state) => void, one of off | listening | thinking | talking
 * @param onPartial  (text) => void, words as they are recognized, for the input box
 * @param notify     (message, bad) => void
 * @param keepAlive  () => boolean, true while the mic must stay open through silence
 * @param lang       () => BCP47 tag
 * @param onUserText (text) => void, live voice lane only: show what the player said
 * @param onBotLine  () => { push, end }, live voice lane only: show her reply as it streams
 * @param agent      the voice agent lane's hooks (voicecf.js), optional: { voice(), onAgent(agent|null),
 *                   onTurn(text) => decision, onMeta(meta), onSaid(text) }. The page's gate and
 *                   handlers, so a turn on the agent runs exactly as it would in the browser lane.
 * @param prepare    (text) => { text, cancel() } | null, optional, streaming ears only: start
 *                   the reply the moment Flux guesses the turn is over (EagerEndOfTurn).
 *                   The handle is passed to ask() as its second argument when the final
 *                   words are the same, and cancelled when the player kept talking
 *                   (TurnResumed), said something else, or the conversation stopped.
 *                   ask() may ignore it; it is cancelled after ask() returns either way,
 *                   which does nothing to a reply that has already been read.
 */
export function createConversation({ ask, isSpeaking, onState, onPartial, notify, lang, keepAlive, onUserText, onBotLine, prepare, agent: hooks }) {
  let on = false, engine = null, ear = null, ears = null, quiet = 0, running = null, rtc = null, early = null, cf = null;
  const dropEarly = () => { const e = early; early = null; try { e?.cancel(); } catch {} };
  const sameWords = (a, b) => { const n = (t) => String(t || '').toLowerCase().replace(/[^a-z0-9 ]+/g, ' ').replace(/\s+/g, ' ').trim(); return n(a) === n(b); };

  const setState = (s) => { try { onState?.(s); } catch {} };
  const cancelled = () => !on;

  /** Do not reopen the mic until the last of her voice has actually stopped. */
  async function settle() {
    const until = performance.now() + 8000;      // she cannot talk forever
    while (on && isSpeaking?.() && performance.now() < until) await sleep(90);
    await sleep(TAIL_MS);
  }

  async function hear() {
    if (engine === 'stream') {
      return ears.turn(onPartial, cancelled, prepare ? {
        onEager: (text) => { dropEarly(); if (!on) return; try { early = prepare(text) || null; } catch { early = null; } },
        onResumed: dropEarly,
      } : undefined);
    }
    if (engine === 'web') return webTurn(lang(), onPartial);
    let got;
    try { got = await ear.turn(null, cancelled); } catch (e) { throw micError(e); }
    if (!got || cancelled()) return '';
    setState('thinking');
    const r = await fetch('/api/listen', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ audio: await toDataUrl(got.blob), voiced: got.voiced }),
    });
    if (!r.ok) {
      const e = new Error((await r.json().catch(() => ({}))).error || 'Could not make that out.');
      e.code = r.status === 429 ? 'rate' : 'listen';
      throw e;
    }
    return String((await r.json()).text || '').trim();
  }

  // A circuit breaker. Whatever else goes wrong, a conversation that produces
  // more than eight turns in twenty seconds is not a conversation, it is a
  // loop, and it stops itself rather than running until somebody hits End.
  const turnTimes = [];
  const runaway = () => { const now = Date.now(); turnTimes.push(now); while (turnTimes.length && now - turnTimes[0] > 20000) turnTimes.shift(); return turnTimes.length > 8; };

  async function loop() {
    while (on) {
      setState('listening');
      let said = '';
      try {
        said = await hear();
      } catch (e) {
        dropEarly();
        // The browser recognizer failing is not the end of the conversation:
        // the recorder works on every device, so drop to it and keep going.
        if (e.code === 'rate') { notify?.(e.message, true); return stop(); }
        // The streaming ears dropped (network, minutes, a deploy): the other
        // ears need nothing from the Worker to start, so the talk goes on.
        if (engine === 'stream' && e.code === 'ears') {
          ears?.close(); ears = null;
          engine = preferRecorder() ? 'recorder' : 'web';
          if (engine === 'recorder') {
            ear ??= createRecorderEar();
            try { await ear.open(); } catch (err) { notify?.(micError(err).message, true); return stop(); }
          }
          continue;
        }
        if (engine === 'web' && e.code !== 'not-allowed' && e.code !== 'service-not-allowed') {
          engine = 'recorder';
          ear ??= createRecorderEar();
          notify?.('Switching to the backup microphone.');
          continue;
        }
        notify?.(e.message, true);
        return stop();
      }
      if (!on) { dropEarly(); break; }
      // The early start is kept only for the words it was started on.
      const head = early && sameWords(early.text, said) ? early : null;
      if (!head) dropEarly();
      early = null;

      if (!said) {
        quiet++;
        // At a park there are long gaps between shots. While a run is live the
        // microphone stays open through them: closing it after half a minute of
        // quiet would mean the player calls out a make and nothing hears it.
        if (quiet >= QUIET_TURNS_TO_STOP && !keepAlive?.()) {
          notify?.('Stopped listening. Tap the mic when you want to talk again.');
          return stop();
        }
        continue;
      }
      quiet = 0;
      if (runaway()) { try { head?.cancel(); } catch {} notify?.('I kept hearing myself, so I stopped listening. Tap the mic to start again.', true); return stop(); }
      setState('thinking');
      try {
        await ask(said, head);
      } catch (e) {
        notify?.(e?.message || 'That did not go through.', true);
        // Nothing the microphone does will fix being out of replies.
        if (e?.code === 'rate') return stop();
      } finally { try { head?.cancel(); } catch {} }
      if (!on) break;
      await settle();
    }
  }

  /**
   * The live voice lane. The node does the listening, thinking and speaking;
   * this tab shows the words through onUserText and onBotLine. Null when it
   * could not connect in time, and the browser lane takes over as if the
   * Worker had said browser.
   */
  async function webrtcLane(session) {
    let line = null;
    try {
      const { connectVoice } = await import('./voicertc.js');
      const lane = await connectVoice({
        url: session.url, token: session.token, timeoutMs: WEBRTC_CONNECT_MS,
        onPartial, onUserText,
        onBotText: (text, done) => {
          if (done) { line?.end(text); line = null; return; }
          line ??= onBotLine?.();
          line?.push(text);
        },
        onState: (s) => { if (on && s !== 'off') setState(s); },
        onEnd: () => {
          // The node hung up: time is up, or the call dropped. Not a stop from here.
          if (!rtc) return;
          rtc = null;
          notify?.('The conversation ended. Tap the mic to start again.');
          stop();
        },
      });
      if (!on) { lane.stop(); return null; }   // the player ended it while it was connecting
      return lane;
    } catch (e) {
      console.warn('live voice unavailable, using the browser', e?.message);
      return null;
    }
  }

  /**
   * The voice agent inside Cloudflare (voicecf.js): hearing, thinking and speaking all run there, the
   * page's gate and handlers decide what each turn does (hooks). Null when it could not connect in
   * time, and the browser lane takes over as if the Worker had said browser. { exhausted } when it
   * refused for minutes.
   */
  async function cfLane(session, my) {
    let line = null;
    try {
      const { connectAgent } = await import('./voicecf.js');
      const lane = await connectAgent({
        url: session.url, token: session.token, transport: session.transport === 'sfu' ? 'sfu' : 'ws',
        voice: hooks?.voice?.() || '', timeoutMs: CF_CONNECT_MS,
        onTurn: (text, id) => hooks?.onTurn?.(text, id),
        onMeta: (meta, id) => hooks?.onMeta?.(meta, id),
        onSaid: (text, id) => hooks?.onSaid?.(text, id),
        onPartial,
        onBotText: (text, done) => {
          if (done) { line?.end(text); line = null; return; }
          line ??= onBotLine?.();
          line?.push(text);
        },
        onState: (s) => { if (on && s !== 'off') setState(s); },
        onEnd: ({ reason, message }) => {
          // The agent hung up. Out of minutes ends the conversation, as the browser lane would; anything
          // else (a dropped connection, a deploy, the idle timer) carries on in the browser lane.
          if (!cf) return;
          cf = null; try { hooks?.onAgent?.(null); } catch {}
          if (reason === 'minutes') { notify?.(message || 'You have used all your talk time for this month.', true); stop(); return; }
          if (reason === 'idle') { notify?.('Stopped listening. Tap the mic when you want to talk again.'); stop(); return; }
          if (!on || my !== gen) return;
          notify?.('Switching to the backup voice.');
          browserLane(my).catch(() => stop());
        },
      });
      if (!on || my !== gen) { lane.stop(); return null; }
      return lane;
    } catch (e) {
      if (e?.reason === 'minutes') return { exhausted: e.message };
      console.warn('voice agent unavailable, using the browser', e?.message);
      return null;
    }
  }

  // Every start and stop takes a new number. A start that was waiting on the
  // network when the player tapped off and on again finds it is no longer the
  // latest and backs out, instead of running a second listener beside the new
  // one (and, on the voice node lane, leaving a call nobody can hang up).
  let gen = 0;
  const current = (my) => on && my === gen;

  async function start() {
    if (on) return true;
    on = true; quiet = 0;
    const my = ++gen;
    // While a run is live, spoken plays must reach game.voiceLine. The browser
    // lane's ask() and the voice agent's gate (hooks.onTurn) both do that; the
    // webrtc node has no gate, so a live run never goes there.
    const session = (await takeEarly()) || await voiceSession();
    if (!current(my)) return false;
    if (session.mode === 'exhausted') {
      on = false;
      setState('off');
      notify?.(session.message, true);
      return false;
    }
    if (session.mode === 'webrtc' && !keepAlive?.()) {
      const lane = await webrtcLane(session);
      if (!current(my)) { lane?.stop(); return false; }
      if (lane) { rtc = lane; return true; }
    }
    // The agent hears and speaks English (Flux, Whisper set to English, Aura); other languages keep
    // the browser lane, as they keep the browser's recognizer or Whisper there.
    if (session.mode === 'cf' && /^en/i.test(lang?.() || 'en')) {
      const lane = await cfLane(session, my);
      if (!current(my)) { lane?.stop?.(); return false; }
      if (lane?.exhausted) { on = false; setState('off'); notify?.(lane.exhausted, true); return false; }
      if (lane) { cf = lane; try { hooks?.onAgent?.(lane); } catch {} return true; }
    }
    return browserLane(my);
  }

  /** The browser lane: the page hears, the Worker thinks, the page speaks. */
  async function browserLane(my) {
    // Flux transcribes English. The other languages keep the browser's
    // recognizer or Whisper, which know them.
    if (streamable() && /^en/i.test(lang?.() || 'en')) {
      const s = createStreamEar();
      try {
        await s.open();
        if (!current(my)) { s.close(); return false; }
        engine = 'stream'; ears = s;
        running = loop().catch((e) => { console.warn('conversation ended', e); stop(); });
        return true;
      } catch (e) {
        s.close();
        if (!current(my)) return false;
        console.warn('streaming ears unavailable, using the next ear', e?.message);
      }
    }
    engine = preferRecorder() ? 'recorder' : 'web';
    if (engine === 'recorder') {
      ear ??= createRecorderEar();
      try {
        // Opened inside the tap that started this, which is what iOS requires.
        await ear.open();
      } catch (e) {
        if (!current(my)) return false;
        on = false;
        setState('off');
        notify?.(micError(e).message, true);
        return false;
      }
      if (!current(my)) return false;
    }
    running = loop().catch((e) => { console.warn('conversation ended', e); stop(); });
    return true;
  }

  function stop() {
    if (!on) return;
    on = false; gen++;
    dropEarly();
    if (rtc) { const lane = rtc; rtc = null; lane.stop(); }
    if (cf) { const lane = cf; cf = null; try { hooks?.onAgent?.(null); } catch {} lane.stop(); }
    ears?.close(); ears = null;
    ear?.close();
    setState('off');
  }

  return {
    start,
    stop,
    toggle: () => (on ? (stop(), false) : start()),
    get live() { return on; },
  };
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
